import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import SongRow from "../common/SongRow";
import { loadSungBefore, loadTopSongs, searchCatalog, setCatalogFilter, setCatalogSearch } from "../../actions/catalogActions";
import { FILTERS } from "../../reducers/catalogReducer";
import { selectIsHost, selectSets } from "../../selectors";

const DEBOUNCE_MS = 400;

const FILTER_NOTES = {
  Yours: "The songs you wrote or imported. Only you find them in the lists; people in your session see them when you queue them.",
  Offline: "Your favourites, kept on this phone when “Keep favorites offline” is on.",
  "Sung before": "Songs from sessions you hosted, minus anything already in a set or in your favourites.",
};

const matches = (song, term) => {
  if (!term) return true;
  const hay = `${song.Title} ${song.Artist || ""}`.toLowerCase();
  return hay.includes(term);
};

/**
 * Songs: search across titles, writers and lyrics; filters; a tap opens the song, hosts
 * mark favourites and bookmark into sets; guests who came from the + button request.
 */
const SongsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isHost = useSelector(selectIsHost);
  const hasKey = useSelector((state) => state.host.hasKey);
  const { searchValue, searchSongs, topSongs, sungBefore, filter, isSearching } = useSelector((state) => state.catalog);
  const favourites = useSelector((state) => state.library.songs);
  const sets = useSelector(selectSets);
  const userId = useSelector((state) => state.host.userId);
  const requestMode = searchParams.get("request") === "1" && !isHost;

  useEffect(() => {
    dispatch(loadTopSongs());
  }, [dispatch]);

  useEffect(() => {
    if (filter === "Sung before") dispatch(loadSungBefore());
  }, [dispatch, filter]);

  useEffect(() => {
    if (!searchValue.trim()) return undefined;
    const handle = setTimeout(() => dispatch(searchCatalog(searchValue)), DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [dispatch, searchValue]);

  const term = searchValue.trim().toLowerCase();
  const inAnySet = (songId) => sets.some((set) => set.Songs.some((song) => song.Id === songId));
  const inFavourites = (songId) => favourites.some((song) => song.Id === songId);

  let songs;
  let emptyNote;
  if (filter === "Yours") {
    const own = [...favourites, ...searchSongs, ...topSongs].filter((song) => song.OwnerId != null && (userId == null || song.OwnerId === userId));
    const seen = new Set();
    songs = own.filter((song) => !seen.has(song.Id) && seen.add(song.Id) && matches(song, term));
    emptyNote = "No songs of your own yet. Write one or import it from a link.";
  } else if (filter === "Offline") {
    songs = favourites.filter((song) => matches(song, term));
    emptyNote = "No favourites yet. Tap the heart on any song.";
  } else if (filter === "Sung before") {
    songs = sungBefore.filter((song) => !inAnySet(song.Id) && !inFavourites(song.Id) && matches(song, term));
    emptyNote = "Everything you have sung is already in a set or favorited.";
  } else {
    songs = term ? searchSongs : topSongs;
    emptyNote = term ? (isSearching ? "Searching…" : "Nothing matches. You can write or import the song instead.") : "Search by title, writer, or any line you remember.";
  }

  const visibleFilters = isHost ? FILTERS : FILTERS.filter((name) => name !== "Yours" && name !== "Sung before");

  return (
    <div className="page" data-screen-label="Songs">
      <div className="page-head">
        <h2 className="page-title">{requestMode ? "Request a song" : "Songs"}</h2>
        {requestMode && <p className="page-sub">Pick one and the host sees it as a one-tap decision.</p>}
      </div>
      <form
        className="search"
        role="search"
        onSubmit={(event) => {
          event.preventDefault();
          dispatch(searchCatalog(searchValue));
        }}
      >
        <span className="glyph" aria-hidden="true">
          ⌕
        </span>
        <input value={searchValue} onChange={(event) => dispatch(setCatalogSearch(event.target.value))} placeholder="Title, lyric, or writer" aria-label="Search songs" autoComplete="off" />
        {searchValue && (
          <button type="button" className="clear" onClick={() => dispatch(setCatalogSearch(""))} aria-label="Clear the search">
            ×
          </button>
        )}
      </form>
      <div className="wrap">
        {visibleFilters.map((name) => (
          <button key={name} type="button" className="filter-chip" aria-pressed={filter === name} onClick={() => dispatch(setCatalogFilter(name))}>
            {name}
          </button>
        ))}
      </div>
      {FILTER_NOTES[filter] && <p className="note">{FILTER_NOTES[filter]}</p>}
      {songs.length > 0 ? (
        <ol className="stack stack-tight">
          {!term && filter === "All" && <li className="kicker">Popular songs</li>}
          {songs.map((song) => (
            <SongRow key={song.Id} song={song} showRequest={requestMode} />
          ))}
        </ol>
      ) : (
        <div className="empty">{emptyNote}</div>
      )}
      {isHost && hasKey && (
        <div className="btn-row">
          <button type="button" className="btn btn-md btn-dashed" onClick={() => navigate("/songs/new")}>
            ＋ Create a song
          </button>
          <button type="button" className="btn btn-md btn-dashed" onClick={() => navigate("/songs/import")}>
            Import from a link
          </button>
        </div>
      )}
    </div>
  );
};

export default SongsPage;
